/**
 * The address the first run puts on a phone, and the seam the signed version of it drops into.
 *
 * Leo, on the desktop half of onboarding: *"If on a desktop device, we encourage mobile install in
 * the same way, show a QR code."* A QR is only ever a URL, so the whole of the design question is
 * **which** URL — and there are two answers, one of which is a credential and one of which is not.
 *
 * ## What this returns today, and why it is the plain one
 *
 * The site's own address, and nothing else. A learner scans it, their phone opens nabu, and they
 * sign in there the way they sign in anywhere: a code to the address they already proved. That is
 * one more step than it could be and it is **zero** new ways to be reachable by somebody who is not
 * the learner.
 *
 * ## The seam
 *
 * The other answer — a link that signs the phone in on arrival — is a **credential minted into a
 * URL**, and a URL is the least protected thing in a browser. It is in the scan history of whatever
 * app read the code, in the phone's address bar, in a `Referer` header if the page ever links out,
 * and in whatever the learner pastes it into. It needs an expiry short enough to be useless later,
 * a single use enforced server-side, a binding to the account that asked for it, and a review of
 * its own. **It is being designed separately and is not in this change.**
 *
 * This function is where it lands. It is the only place the QR's contents are decided, it takes the
 * origin rather than reading `window`, and it is asserted from plain Node — so the signed version
 * is a change to one function with a test already pointed at it. What the caller must not do is
 * build the URL itself: two places deciding this is how the plain one survives in a corner
 * somebody forgot, or how the signed one reaches a screen nobody reviewed it for.
 *
 * `packages/web/test/onboarding-handoff.test.ts` asserts that what comes back carries no query and
 * no fragment. That test is not decoration and it is not about tidiness: it is the thing that fails
 * on the day somebody adds a token here without the rest of the design, which is exactly the change
 * that must not arrive quietly.
 */

/**
 * The URL to encode in the QR, given this browser's origin.
 *
 * Takes the origin instead of reading `location` so it can be asserted without a DOM, for
 * `install/route.ts`'s reason: the last platform decision in this codebase that lived inside a
 * component shipped four wrong fixes before anybody measured anything.
 *
 * A non-`https` origin answers `undefined` rather than a URL. Nothing about nabu works over plain
 * http — service workers, push and the install prompt all require a secure context — so a QR
 * pointing at one is a picture that leads to a broken app, and the caller shows the address as text
 * instead. In practice this is a developer on `http://localhost`, which is the one place it costs
 * nothing to be strict.
 */
export function handoffUrl(origin: string): string | undefined {
  if (!origin.startsWith('https://')) return undefined;
  // A trailing slash, so the QR carries a complete address rather than a bare host that some
  // scanners offer as a search term.
  return origin.endsWith('/') ? origin : `${origin}/`;
}
